import * as React from 'react';
import { DocumentItem } from './DocumentItem';
import { Document } from '@/types/document';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, FileText, CheckCircle, XCircle, Clock, Upload } from 'lucide-react';

interface DocumentListProps {
  documents: Document[];
  summary: {
    total: number;
    required: number;
    optional: number;
    uploaded: number;
    pending: number;
    approved: number;
    rejected: number;
    missingRequired?: number;
  };
  missingRequired?: Array<{ documentType: string; label: string }>;
  onApprove: (
    documentId: string,
    notes?: string,
    target?: 'document' | 'pending_update',
  ) => void;
  onReject: (
    documentId: string,
    reason: string,
    notes?: string,
    target?: 'document' | 'pending_update',
  ) => void;
  onView: (document: Document, opts?: { pending?: boolean }) => void;
  onRefresh: () => void;
  isUpdating: boolean;
  isLoading: boolean;
  /** When set, only the matching document row shows a loading state on View. */
  viewingDocumentId?: string | null;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  summary,
  missingRequired,
  onApprove,
  onReject,
  onView,
  onRefresh,
  isUpdating,
  isLoading,
  viewingDocumentId = null,
}) => {
  const [updatingDocuments, setUpdatingDocuments] = React.useState<Set<string>>(new Set());

  const handleApprove = async (
    documentId: string,
    notes?: string,
    target?: 'document' | 'pending_update',
  ) => {
    setUpdatingDocuments(prev => new Set(prev).add(documentId));
    try {
      await onApprove(documentId, notes, target);
    } finally {
      setUpdatingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const handleReject = async (
    documentId: string,
    reason: string,
    notes?: string,
    target?: 'document' | 'pending_update',
  ) => {
    setUpdatingDocuments(prev => new Set(prev).add(documentId));
    try {
      await onReject(documentId, reason, notes, target);
    } finally {
      setUpdatingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const getCompletionPercentage = () => {
    if (summary.uploaded === 0) return 0;
    return Math.min(
      100,
      Math.round((summary.approved / summary.uploaded) * 100)
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Student Documents</h3>
          <p className="text-sm text-muted-foreground">
            Manage and review student document submissions
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isUpdating}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isUpdating ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap text-xs font-medium">Uploaded</span>
          </div>
          <p className="mt-1 text-2xl font-bold leading-none">{summary.uploaded}</p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-1.5 text-green-600">
            <CheckCircle className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap text-xs font-medium">Approved</span>
          </div>
          <p className="mt-1 text-2xl font-bold leading-none text-green-600">
            {summary.approved}
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-1.5 text-blue-600">
            <Clock className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap text-xs font-medium">Pending review</span>
          </div>
          <p className="mt-1 text-2xl font-bold leading-none text-blue-600">
            {summary.pending}
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-1.5 text-amber-600">
            <Upload className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap text-xs font-medium">Pending upload</span>
          </div>
          <p className="mt-1 text-2xl font-bold leading-none text-amber-600">
            {summary.missingRequired ?? 0}
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-center gap-1.5 text-red-600">
            <XCircle className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap text-xs font-medium">Rejected</span>
          </div>
          <p className="mt-1 text-2xl font-bold leading-none text-red-600">
            {summary.rejected}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Document Completion</span>
          <span className="text-muted-foreground">{getCompletionPercentage()}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${getCompletionPercentage()}%` }}
          />
        </div>
      </div>

      {/* Uploaded Documents List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">Uploaded documents</h4>
          <span className="text-xs text-muted-foreground">
            {documents.length} {documents.length === 1 ? 'file' : 'files'}
          </span>
        </div>
        {documents.length === 0 ? (
          <div className="rounded-lg border border-dashed py-8 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Student has not uploaded any documents yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {documents.map((document) => (
              <DocumentItem
                key={document._id}
                document={document}
                onApprove={handleApprove}
                onReject={handleReject}
                onView={onView}
                isUpdating={updatingDocuments.has(document._id)}
                isViewing={viewingDocumentId === document._id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Awaiting upload from student */}
      {missingRequired && missingRequired.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">
              Awaiting upload from student
            </h4>
            <Badge
              variant="outline"
              className="border-amber-200 bg-amber-50 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
            >
              {missingRequired.length} required pending
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            The student has not uploaded these required documents yet. They will
            appear in the list above once submitted.
          </p>
          <div className="space-y-2">
            {missingRequired.map((m) => (
              <div
                key={m.documentType}
                className="flex items-center justify-between gap-3 rounded-md border border-dashed border-amber-200 bg-amber-50/40 px-3 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/20"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <Upload className="h-4 w-4 shrink-0 text-amber-600" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {m.label || m.documentType}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Required · {m.documentType}
                    </p>
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="shrink-0 border-amber-200 bg-amber-50 text-[10px] uppercase tracking-wide text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
                >
                  Not uploaded
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Required vs Optional Summary (this student's uploads) */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">Required</Badge>
            <span className="font-medium">{summary.required}</span>
          </div>
          <p className="text-muted-foreground">Required uploads</p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">Optional</Badge>
            <span className="font-medium">{summary.optional}</span>
          </div>
          <p className="text-muted-foreground">Optional uploads</p>
        </div>
      </div>
    </div>
  );
};
